/**
 * lib/updatePolicy.ts
 * ------------------------------------------------------------------
 * WHEN THE PORTAL MAY RELOAD ITSELF ONTO A NEW BUILD — the rules, with
 * no browser in them, so they are tested as sentences.
 *
 * Staff reported the app reloading itself a few minutes after a
 * Ctrl+Shift+R, with no release due (Oct 2026). Three things combined:
 *
 *   - any service-worker takeover counted as "a new version", without
 *     asking whether this page was actually behind;
 *   - "a new version" was applied the next time the tab gained focus —
 *     the very moment the person came back to work on it;
 *   - and when the takeover it waited for never came, it wiped every
 *     cache and reloaded anyway.
 *
 * So now a signal from the service worker is a REASON TO CHECK, not an
 * order to reload. The page reloads only when it is known to be older
 * than what the server is serving (or the server cannot be asked and the
 * worker says so), only at a quiet moment, and never over unsaved work.
 * lib/swUpdate.ts and hooks/useVersionGate.ts carry these rules out.
 * ------------------------------------------------------------------ */

/** A page that loaded without a worker this recently is already the newest code. */
export const FRESH_LOAD_MS = 10 * 60_000;

/** Reading is not idle. Five minutes without a key or a click is. */
export const QUIET_AFTER_MS = 5 * 60_000;

/**
 * Is a client on `client` older than `server`?
 *
 * true / false when that is known; null when it is not (either id missing,
 * or in a shape that cannot be ordered). Never "not equal means behind": a
 * client loaded between the hosting deploy and the build-id publish is
 * NEWER than the published id, and must not be told to reload.
 */
export function isBehind(client: string | null | undefined, server: string | null | undefined): boolean | null {
  if (!client || !server) return null;
  if (client === 'dev') return false;
  if (client === server) return false;
  const ts = /^\d{14}$/;
  if (ts.test(client) && ts.test(server)) return client < server; // YYYYMMDDHHMMSS: lexical = chronological
  const num = /^\d+$/;
  if (num.test(client) && num.test(server)) return Number(client) < Number(server);
  return null;
}

/** The newest of several build ids (any that cannot be ordered are skipped). */
export function newestBuild(...ids: (string | null | undefined)[]): string | null {
  let best: string | null = null;
  for (const id of ids) {
    if (!id) continue;
    if (best === null || isBehind(best, id) === true) best = id;
  }
  return best;
}

export interface PageLoad {
  /** navigator.serviceWorker.controller existed when the page loaded. */
  loadedWithController: boolean;
  loadedAt: number;
  now: number;
}

/** Loaded straight from the server (hard refresh, first visit) a moment ago. */
export function loadedFreshFromServer(p: PageLoad): boolean {
  return !p.loadedWithController && p.now - p.loadedAt < FRESH_LOAD_MS;
}

/**
 * A worker has taken control of this page. Ignore it, or treat it as a
 * possible update (which is then checked against the build id)?
 */
export function onControllerChange(p: PageLoad & {
  /** We sent SKIP_WAITING ourselves to swap quietly: this change is ours. */
  expectingQuietSwap: boolean;
  /** Changes seen before this one on this page. */
  earlierChanges: number;
}): 'ignore' | 'check' {
  if (p.expectingQuietSwap) return 'ignore';
  // Ctrl+Shift+R and a first visit: the page came from the server and is
  // already the newest code; the worker claiming it is not news. But a page
  // open for a long time may be asking for files the server no longer has,
  // so after a while the takeover is followed like any other.
  if (p.earlierChanges === 0 && loadedFreshFromServer(p)) return 'ignore';
  return 'check';
}

/**
 * A new worker is installed and waiting. Switch it on quietly (no reload),
 * or treat it as an update for this page?
 */
export function onWaitingWorker(p: PageLoad & {
  /** Is this page behind the newest known build? (isBehind) */
  behind: boolean | null;
}): 'swap-quietly' | 'update' {
  if (p.behind === false) return 'swap-quietly';
  if (p.behind === null && loadedFreshFromServer(p)) return 'swap-quietly';
  return 'update';
}

/** Is this page out of date, all things considered? */
export function needsUpdate(s: {
  behind: boolean | null;
  /** The worker signalled a possible update, and nothing contradicted it. */
  workerSignal: boolean;
  /** A lazy-loaded file failed to load — its build is gone from the server. */
  chunkMissing: boolean;
}): boolean {
  if (s.chunkMissing) return true;
  if (s.behind !== null) return s.behind;
  return s.workerSignal;
}

/** Is now a moment to reload without anybody losing anything? */
export function quietMoment(s: {
  hidden: boolean;
  msSinceInput: number;
  unsavedWork: boolean;
  /** Something is under way that a reload would interrupt (a send, an upload). */
  busy: boolean;
}): boolean {
  if (s.unsavedWork || s.busy) return false;
  if (s.hidden) return true;
  return s.msSinceInput >= QUIET_AFTER_MS;
}
